import { unpackToDateTime } from '@campfire/hot-date';
import React from 'react';
import { Box, Tooltip, Typography } from '@material-ui/core';
import { DataGrid, GridCellParams, GridColumns } from '@material-ui/data-grid';
import { CommonRequiringActionWidgetProps } from '../../../shared';
import { VolunteerProfile } from '../../../../../../../../../common/AvatarName';
import { useCampfireQuery } from '../../../../../../../../../global/network/useCampfireQuery';
import { ActivityNameAndPriority } from '../../../../../../../../program-manager/volunteers/common/ActivityInfo';
import { GET_TABLE_WIDGET_ACTIVITY_APPLICATIONS } from './activity-application-table-widget-model.gql';
import {
  GetTableWidgetActivityApplications,
  GetTableWidgetActivityApplicationsVariables,
} from './__generated__/GetTableWidgetActivityApplications';
import { useApplyCustomWords } from '../../../../useCustomWords';
import { WidgetTypes } from '../../../../dashboard-widget-setting/dashboard-widget-model';

export const ActivityApplicationTableWidget = (props: CommonRequiringActionWidgetProps) => {
  const cw = useApplyCustomWords();
  const { filters, range } = props;
  const { programIds, activityIds } = JSON.parse(filters) as { programIds: [string]; activityIds: [string] };

  const { data, loading } = useCampfireQuery<
    GetTableWidgetActivityApplications,
    GetTableWidgetActivityApplicationsVariables
  >(GET_TABLE_WIDGET_ACTIVITY_APPLICATIONS, {
    options: {
      variables: {
        programIds: programIds.length > 0 ? programIds : null,
        activityIds: activityIds.length > 0 ? activityIds : null,
        range: range,
        widgetType: WidgetTypes.TABLE,
      },
    },
  });

  const applications =
    data?.vm.activityApplications
      .filter(
        (application) =>
          application.activity.isActive &&
          !application.activity.program.dateDeleted &&
          !application.activity.program.dateSuspended &&
          !application.volunteer.dateDeactivated &&
          !application.volunteer.flagging?.dateFlagged
      )
      .map((application) => ({
        id: application.activityApplicationId,
        program: application.activity.program.name,
        profile: application.volunteer.profile,
        activityName: application.activity.name,
        activityPriority: application.activity.priority,
        dateApplied: unpackToDateTime(application.dateApplied).toFormat('dd/MM/y'),
        dateWithdrawn: application.dateWithdrawn ? unpackToDateTime(application.dateWithdrawn).toFormat('dd/MM/y') : '',
        dateActioned: application.dateActioned ? unpackToDateTime(application.dateActioned).toFormat('dd/MM/y') : '',
      })) || [];

  const columns: GridColumns = [
    {
      field: 'volunteerName',
      headerName: 'Volunteer',
      flex: 1,
      disableColumnMenu: true,
      minWidth: 180,
      renderCell: (params: GridCellParams) => {
        const { row } = params;
        return (
          <VolunteerProfile inTable {...row.profile} name={`${row.profile.preferredName} ${row.profile.lastName}`} />
        );
      },
    },
    {
      field: 'program',
      headerName: cw('Program'),
      flex: 1,
      disableColumnMenu: true,
      minWidth: 150,
      renderCell: ({ row }: any) => {
        return (
          <Tooltip title={row.program}>
            <Typography noWrap style={{ fontSize: 'inherit' }}>
              {row.program}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'activityName',
      headerName: cw('Activity'),
      flex: 1,
      disableColumnMenu: true,
      minWidth: 150,
      renderCell: ({ row }: any) => {
        return <ActivityNameAndPriority name={row.activityName} priority={row.activityPriority} />;
      },
    },
    {
      field: 'dateApplied',
      headerName: 'Date Applied',
      flex: 1,
      disableColumnMenu: true,
      minWidth: 150,
    },
    {
      field: 'dateWithdrawn',
      headerName: 'Date Withdrawn',
      flex: 1,
      disableColumnMenu: true,
      minWidth: 170,
    },
    {
      field: 'dateActioned',
      headerName: 'Date Actioned',
      flex: 1,
      disableColumnMenu: true,
      minWidth: 150,
    },
  ];

  return (
    <Box width='100%' height='100%'>
      <DataGrid columns={columns} rows={applications} loading={loading} autoPageSize disableSelectionOnClick />
    </Box>
  );
};
